import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { MessageCircle, Plus, BellRing, BellOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { playReplyChime, primeReplyChime } from "@/components/symptom-sniffer/audio";
import NewChatDialog from "@/components/chat/NewChatDialog";
import {
  chatNotifEnabled,
  getNotificationPermission,
  showChatNotification,
  vibratePattern,
} from "@/lib/chatNotify";
import { isBackgroundPushSupported, subscribeAndStorePush } from "@/lib/webPush";

interface ConvRow {
  id: string;
  last_message_at: string;
  last_message_preview: string | null;
}
interface PartnerInfo {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
}
interface ConvDisplay extends ConvRow {
  partner: PartnerInfo | null;
  unread: number;
  lastReadAt: string;
}

const MessageBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [convs, setConvs] = useState<ConvDisplay[]>([]);
  const [jingleKey, setJingleKey] = useState(0);
  const [notifPerm, setNotifPerm] = useState<NotificationPermission | "unsupported">(
    getNotificationPermission(),
  );
  const [notifOn, setNotifOn] = useState(chatNotifEnabled());
  const initialLoadRef = useRef(true);
  const activeConvRef = useRef<string | null>(null);

  // Track which conversation the user is currently viewing so we don't notify them about it.
  useEffect(() => {
    if (location.pathname.startsWith("/messages")) {
      const params = new URLSearchParams(location.search);
      activeConvRef.current = params.get("c");
    } else {
      activeConvRef.current = null;
    }
  }, [location.pathname, location.search]);

  useEffect(() => { primeReplyChime(); }, []);

  // If the user already granted permission previously, make sure their
  // push subscription is on file with the server (no-op in preview/iframe).
  useEffect(() => {
    if (!user) return;
    if (chatNotifEnabled() && isBackgroundPushSupported()) {
      subscribeAndStorePush().catch(() => {});
    }
  }, [user]);

  const fetchAll = async () => {
    if (!user) return;
    const { data: parts } = await supabase
      .from("conversation_participants")
      .select("conversation_id, last_read_at, hidden_at")
      .eq("user_id", user.id)
      .is("hidden_at", null);
    const ids = (parts || []).map((p) => p.conversation_id);
    if (ids.length === 0) { setConvs([]); return; }

    const lastRead = new Map((parts || []).map((p) => [p.conversation_id, p.last_read_at]));

    const { data: cs } = await supabase
      .from("conversations")
      .select("id, last_message_at, last_message_preview")
      .in("id", ids)
      .order("last_message_at", { ascending: false })
      .limit(15);

    const { data: others } = await supabase
      .from("conversation_participants")
      .select("conversation_id, user_id")
      .in("conversation_id", ids)
      .neq("user_id", user.id);

    const partnerIds = Array.from(new Set((others || []).map((o) => o.user_id)));
    const { data: profiles } = partnerIds.length
      ? await supabase.rpc("get_chat_partner_profiles", { _user_ids: partnerIds })
      : { data: [] as any[] };

    const partnerByConv = new Map<string, PartnerInfo>();
    (others || []).forEach((o) => {
      const p = (profiles || []).find((pr: any) => pr.user_id === o.user_id);
      const fullName = [p?.first_name, p?.last_name].filter(Boolean).join(" ").trim();
      partnerByConv.set(o.conversation_id, {
        user_id: o.user_id,
        display_name:
          fullName ||
          (p?.display_name as string) ||
          "Unknown",
        avatar_url: (p?.avatar_url as string) || null,
      });
    });

    // unread counts + latest non-deleted preview (so deleted messages drop out of the bell)
    const unreadCounts = new Map<string, number>();
    const previews = new Map<string, string | null>();
    await Promise.all(
      (cs || []).map(async (c) => {
        const lr = lastRead.get(c.id);
        const { count } = await supabase
          .from("messages")
          .select("id", { count: "exact", head: true })
          .eq("conversation_id", c.id)
          .neq("sender_id", user.id)
          .is("deleted_at", null)
          .gt("created_at", lr || "1970-01-01");
        unreadCounts.set(c.id, count || 0);

        const { data: latest } = await supabase
          .from("messages")
          .select("content, attachment_name, deleted_at")
          .eq("conversation_id", c.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (latest) {
          previews.set(
            c.id,
            latest.deleted_at
              ? "This message was deleted"
              : latest.content || (latest.attachment_name ? `📎 ${latest.attachment_name}` : c.last_message_preview),
          );
        }
      }),
    );

    setConvs(
      (cs || []).map((c) => ({
        ...c,
        last_message_preview: previews.has(c.id) ? previews.get(c.id) ?? c.last_message_preview : c.last_message_preview,
        partner: partnerByConv.get(c.id) || null,
        unread: unreadCounts.get(c.id) || 0,
        lastReadAt: lastRead.get(c.id) || "1970-01-01",
      })),
    );
  };

  useEffect(() => {
    if (!user) return;
    fetchAll();

    const ch = supabase
      .channel(`user-messages-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        async (payload) => {
          const m = payload.new as {
            id: string;
            conversation_id: string;
            sender_id: string;
            content: string | null;
            attachment_name: string | null;
          };
          // only react to convs we're in
          const { data } = await supabase
            .from("conversation_participants")
            .select("conversation_id")
            .eq("conversation_id", m.conversation_id)
            .eq("user_id", user.id)
            .maybeSingle();
          if (!data) return;
          fetchAll();
          if (m.sender_id !== user.id && !initialLoadRef.current) {
            playReplyChime();
            setJingleKey((k) => k + 1);

            const isViewingThisChat =
              activeConvRef.current === m.conversation_id &&
              document.visibilityState === "visible" &&
              document.hasFocus();

            if (!isViewingThisChat) {
              vibratePattern([90, 50, 90]);
              // Look up sender name for a friendlier notification.
              const { data: profRows } = await supabase
                .rpc("get_chat_partner_profiles", { _user_ids: [m.sender_id] });
              const prof = (profRows as any[] | null)?.[0];
              const senderFull = [prof?.first_name, prof?.last_name].filter(Boolean).join(" ").trim();
              const name =
                senderFull ||
                (prof?.display_name as string) ||
                "New message";
              const body =
                m.content?.slice(0, 140) ||
                (m.attachment_name ? `📎 ${m.attachment_name}` : "Sent you a message");
              showChatNotification({
                title: name,
                body,
                tag: `chat-${m.conversation_id}`,
                icon: (prof?.avatar_url as string) || "/favicon.ico",
                onClick: () => navigate(`/messages?c=${m.conversation_id}`),
              });
            }
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "messages" },
        async (payload) => {
          // A message was hard-deleted - if it was in one of our conversations,
          // refresh so the unread badge drops in step with the chat view.
          const m = payload.old as { conversation_id?: string };
          if (!m?.conversation_id) { fetchAll(); return; }
          const { data } = await supabase
            .from("conversation_participants")
            .select("conversation_id")
            .eq("conversation_id", m.conversation_id)
            .eq("user_id", user.id)
            .maybeSingle();
          if (data) fetchAll();
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "messages" },
        async (payload) => {
          // Soft-deletes (deleted_at set) and edits arrive as UPDATEs -
          // keep unread counts and previews in sync either way.
          const m = payload.new as { conversation_id?: string };
          if (!m?.conversation_id) return;
          const { data } = await supabase
            .from("conversation_participants")
            .select("conversation_id")
            .eq("conversation_id", m.conversation_id)
            .eq("user_id", user.id)
            .maybeSingle();
          if (data) fetchAll();
        },
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") initialLoadRef.current = false;
      });
    return () => { supabase.removeChannel(ch); };
  }, [user]);

  const totalUnread = convs.reduce((s, c) => s + c.unread, 0);

  if (!user) return null;

  const timeAgo = (s: string) => {
    const diff = Date.now() - new Date(s).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "now";
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
  };

  const openConv = (id: string) => {
    setOpen(false);
    navigate(`/messages?c=${id}`);
  };

  return (
    <>
      <Popover open={open} onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          // Re-read in case the user changed it from Settings.
          setNotifPerm(getNotificationPermission());
          setNotifOn(chatNotifEnabled());
        }
      }}>
        <PopoverTrigger asChild>
          <Button
            key={`msg-btn-${jingleKey}`}
            variant="outline"
            size="icon"
            className={cn("rounded-full relative", jingleKey > 0 && "animate-bell-glow")}
            aria-label="Messages"
          >
            <MessageCircle
              key={`msg-icon-${jingleKey}`}
              className={cn("w-4 h-4 origin-top", jingleKey > 0 && "animate-bell-jingle")}
            />
            {totalUnread > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-[10px] flex items-center justify-center font-bold">
                {totalUnread > 9 ? "9+" : totalUnread}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h4 className="font-semibold text-sm text-foreground">Messages</h4>
            <button
              onClick={() => { setOpen(false); setNewOpen(true); }}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> New chat
            </button>
          </div>
          {notifPerm !== "unsupported" && (
            <div className="px-4 py-2 border-b bg-muted/30 flex items-center justify-between gap-2">
              <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
                {notifPerm === "granted" && notifOn ? (
                  <><BellRing className="w-3 h-3" /> Chat alerts on</>
                ) : notifPerm === "denied" ? (
                  <><BellOff className="w-3 h-3" /> Notifications blocked</>
                ) : (
                  <><BellOff className="w-3 h-3" /> Chat alerts off</>
                )}
              </span>
              <button
                onClick={() => { setOpen(false); navigate("/settings#chat-notifications"); }}
                className="text-[11px] text-primary hover:underline"
              >
                Manage in Settings
              </button>
            </div>
          )}
          <div className="max-h-80 overflow-y-auto">
            {convs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No conversations yet</p>
            ) : (
              convs.map((c) => (
                <button
                  key={c.id}
                  onClick={() => openConv(c.id)}
                  className={cn(
                    "w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-muted/50 transition-colors flex gap-3 items-start",
                    c.unread > 0 && "bg-primary/5",
                  )}
                >
                  <Avatar className="w-9 h-9 shrink-0">
                    {c.partner?.avatar_url && (
                      <AvatarImage src={c.partner.avatar_url} alt={c.partner.display_name} />
                    )}
                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                      {(c.partner?.display_name || "U").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={cn("text-sm truncate", c.unread > 0 ? "font-semibold" : "font-medium")}>
                        {c.partner?.display_name || "User"}
                      </p>
                      <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(c.last_message_at)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {c.last_message_preview || "No messages yet"}
                    </p>
                  </div>
                  {c.unread > 0 && (
                    <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-2" />
                  )}
                </button>
              ))
            )}
          </div>
          <div className="border-t px-4 py-2 text-center">
            <button
              onClick={() => { setOpen(false); navigate("/messages"); }}
              className="text-xs text-primary hover:underline"
            >
              Open inbox
            </button>
          </div>
        </PopoverContent>
      </Popover>
      <NewChatDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(id) => navigate(`/messages?c=${id}`)} />
    </>
  );
};

export default MessageBell;
